import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@/hooks/useWallet';
import { useGameState } from '@/hooks/useGameState';
import { useContract } from '@/hooks/useContract';
import { useGameContext } from '@/contexts/GameContext';
import WalletButton from '@/components/web3/WalletButton';
import NetworkBadge from '@/components/web3/NetworkBadge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { GameCanvas, useGameEngine } from '@/engine/GameCanvas';
import { WorldScene } from '@/engine/WorldScene';
import { BattleScene, BattleResult, PartyMember } from '@/engine/BattleScene';
import { MenuScene, MenuStateResult } from '@/engine/MenuScene';
import { SpriteManager } from '@/engine/SpriteManager';
import { GameScene } from '@/engine/types/engine';
import { GameEngine } from '@/engine/GameEngine';
import { CLASS_INFO, Character, GameState } from '@/lib/types/game';
import { BattleEnemy } from '@/engine/types/engine';
import { getItem } from '@/engine/data/ItemData';

// Enemy name lookup (matches EnemyData.sol IDs)
const ENEMY_NAMES: Record<number, string> = {
  1: 'Goblin', 2: 'Wolf', 3: 'Zombie', 4: 'Skeleton', 5: 'Imp',
  6: 'Ogre', 7: 'GrOgre', 8: 'Ghost', 9: 'Cobra', 10: 'Spider',
  11: 'Warg', 12: 'Ghoul', 13: 'Wraith', 14: 'Mummy', 15: 'Specter',
  16: 'Minotaur', 17: 'Gargoyle', 18: 'Manticore', 19: 'Chimera', 20: 'Vampire',
  36: 'Garland', 37: 'Pirate', 38: 'Astos', 39: 'Lich', 40: 'Kary',
  41: 'Kraken', 42: 'Tiamat', 43: 'Chaos',
};

// Convert enemy ID to BattleEnemy object
const enemyIdToBattleEnemy = (id: number, index: number): BattleEnemy => ({
  id,
  name: ENEMY_NAMES[id] || `Enemy ${id}`,
  hp: 50 + id * 10, // Placeholder HP based on ID
  maxHP: 50 + id * 10,
  attack: 10 + id * 2, // Attack scales with ID
  defense: 5 + id, // Defense scales with ID
  magicDefense: 10 + id * 2, // Magic defense scales with ID
  agility: 5 + id, // Agility scales with ID
  x: 60 + (index % 3) * 60,
  y: 40 + Math.floor(index / 3) * 50,
  spriteId: `enemy_${id}`,
  dead: false,
});

// Starting spells by class (FF1-authentic)
// White Mage Level 1: CURE (1), HARM (2), FOG (3), RUSE (4)
// Black Mage Level 1: FIRE (33), LIT (34), SLEP (35), LOCK (36)
// Red Mage Level 1: CURE (1), FIRE (33)
const getStartingSpells = (classType: number): number[] => {
  switch (classType) {
    case 4: // White Mage
    case 10: // White Wizard
      return [1, 2, 3, 4]; // CURE, HARM, FOG, RUSE
    case 5: // Black Mage
    case 11: // Black Wizard
      return [33, 34, 35, 36]; // FIRE, LIT, SLEP, LOCK
    case 3: // Red Mage
    case 9: // Red Wizard
      return [1, 33]; // CURE, FIRE
    default:
      return []; // Fighter, Thief, Monk - no spells
  }
};

// Get starting spell charges by class
// NOTE: The SpellSystem expects charges stored by SPELL ID, not spell level
// Each spell gets its own charge counter (FF1-authentic: level-based pooling
// would require SpellSystem refactor, so we give per-spell charges for now)
const getStartingSpellCharges = (classType: number): Map<number, number> => {
  const charges = new Map<number, number>();
  const spells = getStartingSpells(classType);

  // Give 3 charges per spell (reasonable starting amount)
  spells.forEach(spellId => {
    charges.set(spellId, 3);
  });

  return charges;
};

// Convert game Character to BattleScene PartyMember format
const characterToPartyMember = (char: Character, index: number): PartyMember => ({
  id: index,
  name: CLASS_INFO[char.class]?.name || `Hero ${index + 1}`,
  class: char.class as any, // Map game class to battle class
  level: char.level,
  hp: char.currentHP,
  maxHP: char.maxHP,
  mp: char.currentMP,
  maxMP: char.maxMP,
  intelligence: char.intelligence,
  spirit: char.intelligence, // FF1 uses INT for both
  status: char.status === 0 ? 'OK' : 'Status',
  experience: char.experience || 0,
  learnedSpells: getStartingSpells(char.class),
  spellCharges: getStartingSpellCharges(char.class),
});

// Convert BattleScene PartyMember back to game Character (after battle)
const partyMemberToCharacter = (member: PartyMember, original: Character): Character => ({
  ...original,
  level: member.level,
  experience: member.experience || original.experience,
  currentHP: member.hp,
  maxHP: member.maxHP,
  currentMP: member.mp,
  maxMP: member.maxMP,
  // Note: Other stats (STR, AGI, etc.) would be updated during level up
});

// Convert battle inventory Map to GameState arrays
const mapToInventoryArrays = (map: Map<number, number>): { inventory: number[], inventoryQty: number[] } => {
  const inventory: number[] = [];
  const inventoryQty: number[] = [];

  map.forEach((qty, id) => {
    if (qty > 0) {
      inventory.push(id);
      inventoryQty.push(qty);
    }
  });

  // Pad arrays to 64 slots (GameState format)
  while (inventory.length < 64) {
    inventory.push(0);
    inventoryQty.push(0);
  }

  return { inventory, inventoryQty };
};

const Game: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected } = useWallet();
  const { gameState, combatState, isLoading, hasGame, isInCombat, refreshState } = useGameState();
  const { setGameState } = useGameContext();
  const { saveGameToChain, isLoading: isSaving } = useContract();
  const { engine, handleEngineReady, isReady: isEngineReady } = useGameEngine();
  const scenesRegistered = useRef(false);
  const spriteManagerRef = useRef<SpriteManager | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  // Track if we've done initial position sync from gameState (to avoid overwriting position after battle)
  const initialPositionSynced = useRef(false);
  // Track the gameState version that we've synced position from (to detect fresh data)
  const lastSyncedStateVersion = useRef<number>(0);
  // Counter to track gameState updates
  const [stateVersion, setStateVersion] = useState(0);
  // Track if we're waiting for fresh data from chain before initializing
  const [waitingForFreshData, setWaitingForFreshData] = useState(true);

  // Store gameState in ref to avoid dependency issues
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  // Store setGameState in ref for callback
  const setGameStateRef = useRef(setGameState);
  setGameStateRef.current = setGameState;

  // Register scenes when engine is ready - use ref to avoid dependency on gameState
  const setupScenes = useCallback((eng: GameEngine) => {
    if (scenesRegistered.current) {
      console.log('[Game] setupScenes: Already registered, skipping');
      return;
    }

    console.log('[Game] setupScenes: Starting scene registration');

    const ctx = eng.getContext();
    console.log('[Game] Got context:', ctx ? 'yes' : 'no');

    // Create sprite manager (shared across scenes)
    if (!spriteManagerRef.current) {
      spriteManagerRef.current = new SpriteManager();
    }
    const spriteManager = spriteManagerRef.current;

    // Create default party for initialization (use ref to get current value)
    const currentGameState = gameStateRef.current;
    const defaultParty: PartyMember[] = currentGameState?.party.map(characterToPartyMember) || [
      { id: 0, name: 'Fighter', class: 0 as any, level: 1, hp: 100, maxHP: 100, mp: 0, maxMP: 0, intelligence: 5, spirit: 5, status: 'OK', experience: 0, learnedSpells: getStartingSpells(0), spellCharges: getStartingSpellCharges(0) },
      { id: 1, name: 'Thief', class: 1 as any, level: 1, hp: 80, maxHP: 80, mp: 0, maxMP: 0, intelligence: 5, spirit: 5, status: 'OK', experience: 0, learnedSpells: getStartingSpells(1), spellCharges: getStartingSpellCharges(1) },
      { id: 2, name: 'W.Mage', class: 4 as any, level: 1, hp: 60, maxHP: 60, mp: 20, maxMP: 20, intelligence: 18, spirit: 18, status: 'OK', experience: 0, learnedSpells: getStartingSpells(4), spellCharges: getStartingSpellCharges(4) },
      { id: 3, name: 'B.Mage', class: 5 as any, level: 1, hp: 50, maxMP: 25, mp: 25, maxHP: 50, intelligence: 20, spirit: 20, status: 'OK', experience: 0, learnedSpells: getStartingSpells(5), spellCharges: getStartingSpellCharges(5) },
    ];

    console.log('[Game] Creating scenes...');

    // Create and register all scenes with required constructor args
    const worldScene = new WorldScene(ctx, spriteManager);
    const battleScene = new BattleScene(ctx, spriteManager, defaultParty);
    const menuScene = new MenuScene(ctx);

    console.log('[Game] Registering scenes...');
    eng.registerScene(GameScene.WORLD, worldScene);
    eng.registerScene(GameScene.BATTLE, battleScene);
    eng.registerScene(GameScene.MENU, menuScene);

    // IMPORTANT: Set initial position BEFORE switching to world scene
    // This ensures the saved position is used instead of the default center position
    if (currentGameState) {
      console.log('[Game] Setting initial position from gameState:', currentGameState.worldX, currentGameState.worldY);
      worldScene.setPosition(currentGameState.worldX, currentGameState.worldY);
    }

    // Set starter inventory for battle scene
    // Item IDs from ItemData.ts: 40=Potion, 41=Hi-Potion, 45=Phoenix Down
    const starterInventory = new Map<number, number>();
    starterInventory.set(40, 5);  // 5 Potions
    starterInventory.set(41, 2);  // 2 Hi-Potions
    starterInventory.set(45, 2);  // 2 Phoenix Downs
    battleScene.setInventory(starterInventory);

    // Initialize MenuScene with party and inventory data
    if (currentGameState?.party) {
      menuScene.setParty([...currentGameState.party]);
    }
    if (currentGameState?.inventory && currentGameState?.inventoryQty) {
      menuScene.setInventory(currentGameState.inventory, currentGameState.inventoryQty);
    } else {
      // Starter inventory for menu scene (same as battle scene)
      const starterIds = [40, 41, 45];
      const starterQty = [5, 2, 2];
      menuScene.setInventory(starterIds, starterQty);
    }

    // ===================================================================
    // WorldScene Callbacks
    // ===================================================================

    // Random encounter callback
    worldScene.onEncounter((enemies: BattleEnemy[], zone: string) => {
      console.log(`[Game] Random encounter! Zone: ${zone}, Enemies:`, enemies.map(e => e.name));

      // Get current party from ref
      const currentState = gameStateRef.current;
      const partyMembers = currentState?.party.map(characterToPartyMember) || defaultParty;

      // Start battle through GameEngine (handles transition properly)
      eng.startBattle(enemies, zone, partyMembers);
    });

    // Location entry callback (town/dungeon)
    worldScene.onEnterLocation((location: any) => {
      console.log(`[Game] Entering location: ${location.name} (${location.type})`);

      if (location.type === 'town') {
        eng.enterTown(location.name);
      } else if (location.type === 'dungeon') {
        eng.enterDungeon(location.name);
      }
    });

    // ===================================================================
    // BattleScene Callbacks
    // ===================================================================

    // Battle end callback - handles victory/defeat/level ups
    battleScene.onBattleEnd((result: BattleResult) => {
      console.log('[Game] Battle ended:', result);

      // Sync battle results back to game state
      const currentState = gameStateRef.current;
      if (currentState && result.updatedParty && result.updatedInventory) {
        // Create updated game state
        const updatedParty = currentState.party.map((char, i) => {
          const partyMember = result.updatedParty[i];
          return partyMember ? partyMemberToCharacter(partyMember, char) : char;
        }) as [Character, Character, Character, Character];

        // Convert inventory map back to arrays
        const { inventory, inventoryQty } = mapToInventoryArrays(result.updatedInventory);

        const newState: GameState = {
          ...currentState,
          party: updatedParty,
          gil: currentState.gil + (result.victory ? result.pasGained : 0), // Add PAS on victory
          inventory,
          inventoryQty,
        };

        // Update local state immediately for responsive UI
        setGameStateRef.current(newState);
        console.log('[Game] State synced - PAS:', newState.gil, 'Party HP:', updatedParty.map(p => p.currentHP));
      }

      eng.returnToWorld(result);
    });

    // ===================================================================
    // MenuScene Callbacks
    // ===================================================================

    // Menu close callback - sync party/inventory state when menu closes
    menuScene.onMenuClose((result: MenuStateResult) => {
      console.log('[Game] Menu closed, syncing state...');

      const currentState = gameStateRef.current;
      if (currentState && result.party && result.inventory) {
        // Map menu Character[] back to game state party tuple
        const updatedParty = result.party.slice(0, 4) as [Character, Character, Character, Character];

        // Pad inventory arrays to 64 slots
        const inventory = [...result.inventory.itemIds];
        const inventoryQty = [...result.inventory.quantities];
        while (inventory.length < 64) {
          inventory.push(0);
          inventoryQty.push(0);
        }

        const newState: GameState = {
          ...currentState,
          party: updatedParty,
          inventory,
          inventoryQty,
        };

        setGameStateRef.current(newState);
        console.log('[Game] Menu state synced - Party HP:', updatedParty.map(p => p.currentHP));
      }
    });

    // ===================================================================
    // Initial Scene
    // ===================================================================

    // Start with world scene
    console.log('[Game] Switching to WORLD scene...');
    eng.switchScene(GameScene.WORLD);

    scenesRegistered.current = true;
    console.log('[Game] Scene registration complete');
  }, []); // No dependencies - uses refs

  // Handle engine ready callback
  const onEngineReady = useCallback((eng: GameEngine) => {
    console.log('[Game] onEngineReady called, scenesRegistered:', scenesRegistered.current);
    handleEngineReady(eng);
    // Reset the flag in case this is a re-render (React Strict Mode)
    scenesRegistered.current = false;
    setupScenes(eng);
  }, [handleEngineReady, setupScenes]);

  // Cleanup on unmount - reset the scenesRegistered flag
  useEffect(() => {
    return () => {
      console.log('[Game] Unmounting - resetting scenesRegistered');
      scenesRegistered.current = false;
    };
  }, []);

  // Force refresh from blockchain on mount to get latest saved state
  const hasRefreshedFromChain = useRef(false);
  useEffect(() => {
    if (hasRefreshedFromChain.current) return;
    if (!isConnected) return;

    hasRefreshedFromChain.current = true;
    // Reset position sync flag so fresh data from chain gets applied
    initialPositionSynced.current = false;
    console.log('[Game] Force refreshing from blockchain on mount...');

    // Call refresh - when it completes, gameState will update and trigger position sync
    refreshState(true).then(() => {
      // Increment state version to signal fresh data arrived
      setStateVersion(v => v + 1);
      setWaitingForFreshData(false);
      console.log('[Game] Refresh complete, fresh data received, waitingForFreshData = false');
    }).catch((err) => {
      console.error('[Game] Refresh failed:', err);
      setWaitingForFreshData(false); // Still allow game to load
    });
  }, [isConnected, refreshState]);

  // Update engine with game state when it changes
  useEffect(() => {
    if (engine && gameState) {
      console.log('[Game] gameState changed - position:', gameState.worldX, gameState.worldY, 'stateVersion:', stateVersion, 'lastSynced:', lastSyncedStateVersion.current);
      engine.loadGameState(gameState);

      // Sync position when we have fresh data from chain (stateVersion changed)
      // After initial sync, WorldScene tracks position locally (to preserve position after battle)

      // Only sync if we haven't synced this version yet
      if (stateVersion > lastSyncedStateVersion.current) {
        engine.setWorldPosition(gameState.worldX, gameState.worldY);
        lastSyncedStateVersion.current = stateVersion;
        initialPositionSynced.current = true;
        console.log('[Game] Position synced from fresh chain data (v' + stateVersion + '):', gameState.worldX, gameState.worldY);
      } else if (!initialPositionSynced.current && stateVersion === 0) {
        // First load before refresh completes - still sync so we have something to show
        engine.setWorldPosition(gameState.worldX, gameState.worldY);
        initialPositionSynced.current = true;
        console.log('[Game] Initial position synced (pre-refresh):', gameState.worldX, gameState.worldY);
      }

      // Update MenuScene with party and inventory data
      const menuScene = engine.getScene<MenuScene>(GameScene.MENU);
      if (menuScene) {
        menuScene.setParty([...gameState.party]);
        if (gameState.inventory && gameState.inventoryQty) {
          menuScene.setInventory(gameState.inventory, gameState.inventoryQty);
        }
      }
    }
  }, [engine, gameState, stateVersion]);

  // Update engine with combat state when it changes
  useEffect(() => {
    if (engine && combatState) {
      engine.loadCombatState(combatState);

      // Switch to battle scene if combat is active
      if (combatState.active && engine.getCurrentScene() !== GameScene.BATTLE) {
        const battleScene = engine['scenes']?.get(GameScene.BATTLE) as BattleScene | undefined;
        if (battleScene && gameState && typeof battleScene.startBattle === 'function') {
          // Convert enemy IDs to BattleEnemy objects
          const enemyIds = combatState.enemyIds.filter((id: number) => id > 0);
          const enemies = enemyIds.map(enemyIdToBattleEnemy);
          const partyMembers = gameState.party.map(characterToPartyMember);
          battleScene.startBattle(enemies, partyMembers);
        }
        engine.setScene(GameScene.BATTLE);
      }
    } else if (engine && !combatState?.active && engine.getCurrentScene() === GameScene.BATTLE) {
      // Combat ended, return to world
      engine.setScene(GameScene.WORLD);
    }
  }, [engine, combatState, gameState]);

  // Redirect if not connected or no game - only check once loading is complete
  const hasRedirectedRef = useRef(false);
  useEffect(() => {
    // Don't redirect while loading
    if (isLoading) return;
    // Don't redirect multiple times
    if (hasRedirectedRef.current) return;

    if (!isConnected) {
      hasRedirectedRef.current = true;
      navigate('/');
    } else if (!hasGame) {
      hasRedirectedRef.current = true;
      navigate('/new-game');
    }
  }, [isConnected, hasGame, isLoading, navigate]);

  // Handle keyboard shortcuts for scene switching (dev mode)
  useEffect(() => {
    if (!engine || process.env.NODE_ENV !== 'development') return;

    const handleDevKeys = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Toggle menu
        if (engine.getCurrentScene() === GameScene.MENU) {
          engine.setScene(GameScene.WORLD);
        } else if (engine.getCurrentScene() === GameScene.WORLD) {
          engine.setScene(GameScene.MENU);
        }
      }
    };

    window.addEventListener('keydown', handleDevKeys);
    return () => window.removeEventListener('keydown', handleDevKeys);
  }, [engine]);

  // Handle saving game to blockchain
  const handleSaveToChain = useCallback(async () => {
    if (!engine || !gameState || isInCombat) {
      console.log('[Game] Cannot save: engine/gameState missing or in combat');
      return;
    }

    setSaveStatus('saving');

    try {
      // Get current game state with synced position from active scene
      const currentState = engine.getCurrentGameState();

      if (!currentState) {
        console.error('[Game] getCurrentGameState returned null');
        setSaveStatus('error');
        return;
      }

      console.log('[Game] Saving to chain with position:', {
        x: currentState.worldX,
        y: currentState.worldY,
      });

      const result = await saveGameToChain(currentState);

      if (result.success) {
        setSaveStatus('success');
        // Reset status after 3 seconds
        setTimeout(() => setSaveStatus('idle'), 3000);
      } else {
        console.error('[Game] Save failed:', result.error);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (err) {
      console.error('[Game] Save error:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [engine, gameState, isInCombat, saveGameToChain]);

  if (isLoading || waitingForFreshData) {
    return (
      <div className="min-h-screen w-full bg-[#1a1a2e] flex items-center justify-center">
        <LoadingSpinner size="lg" text={waitingForFreshData ? "Loading saved game from blockchain..." : "Loading game..."} fullScreen />
      </div>
    );
  }

  if (!gameState) {
    return null;
  }

  return (
    <div className="min-h-screen w-full bg-[#1a1a2e] flex flex-col">
      {/* Header */}
      <header className="bg-[#0f3460] border-b-2 border-[#533483] p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h1 className="text-pixel-base text-[#e94560]">Crystals of Polkadot</h1>
          <div className="flex items-center gap-4">
            <NetworkBadge />
            <WalletButton />
          </div>
        </div>
      </header>

      {/* Main game area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - Party status (collapsible on smaller screens) */}
        <aside className="w-64 xl:w-72 bg-[#16213e] border-r-2 border-[#533483] p-3 overflow-y-auto flex-shrink-0 hidden lg:block">
          {/* Currency display - prominent at top */}
          <Card className="mb-4">
            <div className="flex items-center justify-between p-2">
              <span className="text-gray-300 text-sm">PAS</span>
              <span className="text-[#ffd700] text-lg font-bold">{gameState.gil.toLocaleString()}</span>
            </div>
          </Card>

          <Card title="Party">
            <div className="space-y-2">
              {gameState.party.map((character, index) => {
                const hpPercent = character.maxHP > 0 ? (character.currentHP / character.maxHP) * 100 : 0;
                const hpColorClass = hpPercent <= 25 ? 'text-red-400' : hpPercent <= 50 ? 'text-yellow-400' : 'text-[#00ff00]';
                const hpBarClass = hpPercent <= 25 ? 'hp-bar-critical' : hpPercent <= 50 ? 'hp-bar-warning' : 'hp-bar';

                return (
                  <div key={index} className="ff-border bg-[#0f3460] p-2 rounded">
                    {/* Header with name + level */}
                    <div className="flex justify-between items-center mb-1.5 pb-1 border-b border-[#533483]/50">
                      <span className="font-bold text-[#e94560] text-sm">
                        {CLASS_INFO[character.class]?.name || `Hero ${index + 1}`}
                      </span>
                      <span className="text-[#ffd700] text-xs">Lv.{character.level}</span>
                    </div>

                    {/* HP Bar */}
                    <div className="mb-1.5">
                      <div className="flex justify-between text-xs mb-0.5 text-gray-300">
                        <span>HP</span>
                        <span className={hpColorClass}>
                          {character.currentHP}/{character.maxHP}
                        </span>
                      </div>
                      <div className="h-2 bg-[#1a1a2e] ff-border rounded-sm overflow-hidden">
                        <div
                          className={`${hpBarClass} h-full transition-all duration-300`}
                          style={{ width: `${hpPercent}%` }}
                        />
                      </div>
                    </div>

                    {/* MP Bar */}
                    <div>
                      <div className="flex justify-between text-xs mb-0.5 text-gray-300">
                        <span>MP</span>
                        <span className="text-[#00aaff]">{character.currentMP}/{character.maxMP}</span>
                      </div>
                      <div className="h-2 bg-[#1a1a2e] ff-border rounded-sm overflow-hidden">
                        <div
                          className="mp-bar h-full transition-all duration-300"
                          style={{ width: `${character.maxMP > 0 ? (character.currentMP / character.maxMP) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Location info - compact */}
          <Card className="mt-3" title="Location">
            <div className="text-xs text-gray-300 p-1">
              <span>({gameState.worldX}, {gameState.worldY})</span>
              <span className="text-gray-500 ml-2">
                {gameState.currentMap === 0 ? 'World' : `Loc ${gameState.currentMap}`}
              </span>
            </div>
          </Card>

          {/* Controls - very compact */}
          <Card className="mt-3" title="Controls">
            <div className="text-xs text-gray-400 p-1 leading-relaxed">
              <span className="text-gray-300">Arrows</span> Move &nbsp;
              <span className="text-gray-300">Z</span> OK &nbsp;
              <span className="text-gray-300">X</span> Back &nbsp;
              <span className="text-gray-300">Enter</span> Menu
            </div>
          </Card>
        </aside>

        {/* Center - Game canvas */}
        <main className="flex-1 flex flex-col">
          {/* Game canvas */}
          <div className="flex-1 bg-black flex items-center justify-center relative">
            <GameCanvas
              width={256}
              height={240}
              scale={2}
              onEngineReady={onEngineReady}
              className="game-canvas"
            />

            {/* Engine loading overlay */}
            {!isEngineReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80">
                <LoadingSpinner text="Initializing engine..." />
              </div>
            )}
          </div>

          {/* Control panel */}
          <div className="bg-[#16213e] border-t-2 border-[#533483] p-4">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex gap-3">
                <Button variant="secondary" size="sm" onClick={() => navigate('/')}>
                  Home
                </Button>
                <Button variant="secondary" size="sm" onClick={() => refreshState()}>
                  Refresh
                </Button>
                {engine && (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => engine.setScene(GameScene.MENU)}
                      disabled={engine.getCurrentScene() === GameScene.MENU}
                    >
                      Menu
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => engine.setScene(GameScene.WORLD)}
                      disabled={engine.getCurrentScene() === GameScene.WORLD}
                    >
                      World
                    </Button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-pixel-sm text-[#eaeaea]">
                  {isInCombat ? '⚔️ In Combat' : '🌍 Exploring'}
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Right sidebar - Actions/Inventory */}
        <aside className="w-56 xl:w-64 bg-[#16213e] border-l-2 border-[#533483] p-3 overflow-y-auto flex-shrink-0">
          <Card title="Actions">
            <div className="space-y-2">
              <Button className="w-full" variant="primary" disabled={isInCombat}>
                Move (Arrows)
              </Button>
              <Button
                className="w-full"
                variant="secondary"
                disabled={isInCombat}
                onClick={() => engine?.setScene(GameScene.MENU)}
              >
                Menu (Enter)
              </Button>
              <Button
                className="w-full"
                variant={saveStatus === 'success' ? 'primary' : saveStatus === 'error' ? 'danger' : 'secondary'}
                disabled={isInCombat || saveStatus === 'saving' || isSaving}
                onClick={handleSaveToChain}
              >
                {saveStatus === 'saving' || isSaving
                  ? '⏳ Saving...'
                  : saveStatus === 'success'
                  ? '✓ Saved!'
                  : saveStatus === 'error'
                  ? '✗ Failed'
                  : 'Save Game'}
              </Button>
            </div>
          </Card>

          {isInCombat && combatState && (
            <Card title="Combat" className="mt-4">
              <div className="space-y-2">
                <p className="text-xs text-gray-400 mb-2">
                  Enemies: {combatState.enemyIds.filter((id: number) => id > 0).length}
                </p>
                <Button className="w-full" variant="danger">
                  Attack (Z)
                </Button>
                <Button className="w-full" variant="primary">
                  Magic
                </Button>
                <Button className="w-full" variant="secondary">
                  Item
                </Button>
                <Button className="w-full" variant="secondary">
                  Flee (X)
                </Button>
              </div>
            </Card>
          )}

          <Card title="Inventory" className="mt-4">
            {gameState.inventory && gameState.inventory.some(id => id > 0) ? (
              <ul className="text-xs space-y-1">
                {gameState.inventory.map((itemId, i) => {
                  if (itemId <= 0) return null;
                  const item = getItem(itemId);
                  const qty = gameState.inventoryQty?.[i] || 1;
                  return (
                    <li key={i} className="flex justify-between text-gray-300">
                      <span>{item?.name || `Item #${itemId}`}</span>
                      <span className="text-gray-500">x{qty}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-game text-sm text-gray-400">
                No items yet
              </p>
            )}
          </Card>

          <Card title="Key Items" className="mt-4">
            <p className="text-game text-sm text-gray-400">
              {gameState.keyItems > 0
                ? `Key items: 0x${gameState.keyItems.toString(16)}`
                : 'No key items yet'}
            </p>
          </Card>
        </aside>
      </div>
    </div>
  );
};

export default Game;
